/**
 * What a Recipe Kitchen round is made of: which dish, which decoys, and where everything lands on
 * the shelf (specs/recipe-mode.md §7).
 *
 * Phaser-free and deterministic under an injected `rng`, like game/pairs/deal.ts — RecipeScene only
 * draws what this decides.
 *
 * **Three things are independently random here**, which is the lesson pairs-mode.md §6.4 paid for:
 * which recipe this round, which decoys fill the shelf, and where the tiles sit. Reusing one
 * shuffle for two of them is the classic bug, and its symptom is answers that cluster — a child
 * learns "the right ones are on the left" instead of learning to read.
 */
import { RECIPE } from '@/game/config';
import {
  RECIPES, NEVER_DEAL, NEVER_DECOY, SHELF_INVENTORY, recipesFor,
  type Recipe,
} from './recipes';

export type Rng = () => number;

/** The shape of one rung of `RECIPE.rounds`, named. */
export interface RoundBand {
  /** Tiles on the shelf, ingredients included. */
  tiles: number;
  /** How long the flame burns. */
  ms: number;
  minIngredients: number;
  maxIngredients: number;
  /** Does the card fold to its title on this rung (§4.3)? */
  folds: boolean;
  /** How long the card stays open before folding. 0 on the rungs where it never does. */
  foldAfterMs: number;
}

/** A dealt round: the dish to cook and the shelf to cook it from, in display order. */
export interface DealtRound {
  recipe: Recipe;
  /** Ingredient ids in the order they are laid out — answers and decoys, shuffled together. */
  shelf: string[];
}

/** The rung for `round`, clamping past the end of the ladder onto its last row. */
export function bandForRound(round: number): RoundBand {
  const rows = RECIPE.rounds;
  const [tiles, ms, minIngredients, maxIngredients, foldAfterMs] =
    rows[Math.min(Math.max(1, round), rows.length) - 1];
  return { tiles, ms, minIngredients, maxIngredients, foldAfterMs, folds: foldAfterMs > 0 };
}

/** Fisher-Yates over a copy. The only shuffle in this file, called once per thing to randomise. */
export function shuffle<T>(items: readonly T[], rng: Rng): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * The dish for this round: one whose ingredient count fits the rung's band and which this run has
 * not cooked yet.
 *
 * **Round 1 is drawn like every other round.** It used to be pinned to one dish — the spaghetti on
 * the treat the child had just spent, which read well as a sentence and badly as a game: every run
 * opened on the same card, and the mode's first impression was a repeat. The ladder still does the
 * ordering that matters, since round 1's band is the three-ingredient one; what changes is only
 * *which* of the easy dishes it is.
 *
 * When the band runs dry `used` is cleared rather than the run being ended, exactly as PairsScene
 * clears its word set: a player who has not lost must not be stopped by the content running out.
 */
export function pickRecipe(
  round: number, used: Set<string>, rng: Rng = Math.random, last?: string, lang?: string,
): Recipe {
  const band = bandForRound(round);
  const fits = (r: Recipe): boolean =>
    r.ingredients.length >= band.minIngredients && r.ingredients.length <= band.maxIngredients;

  // The language's own book — the shared recipes plus its national dishes (recipes.ts). Undefined
  // means "everything", which is what the tests and any caller with no lesson in hand get.
  const book = lang === undefined ? RECIPES : recipesFor(lang);
  const inBand = book.filter(fits);
  let candidates = inBand.filter((r) => !used.has(r.id));
  if (candidates.length === 0) {
    // Band exhausted: forget only what this band covers, so a rung the player has not reached
    // keeps its own "not cooked yet" list intact.
    for (const r of inBand) used.delete(r.id);
    candidates = inBand;
  }
  // A band with nothing in it at all would be an authoring error; fall back to the whole book.
  if (candidates.length === 0) candidates = book.slice();

  // Never the dish just cooked, whatever else is true. The reset above is exactly where a repeat
  // used to appear back-to-back: the band empties, everything becomes eligible again, and the most
  // recent dish is as likely as any other. Dropped only while something else remains, so a
  // one-recipe band still deals rather than failing.
  if (last !== undefined && candidates.length > 1) {
    const others = candidates.filter((r) => r.id !== last);
    if (others.length > 0) candidates = others;
  }

  return shuffle(candidates, rng)[0];
}

/**
 * Everything that may stand on the shelf beside `recipe`'s own ingredients.
 *
 * Four exclusions, in order of how badly they bite:
 *  1. `NEVER_DEAL` — the corn, which is a life (recipes.ts).
 *  2. the recipe's own ingredients — a duplicate tile is not a decoy, it is a second right answer.
 *  3. `recipe.forbid` — the single-answer rule of §6.5: nothing the pictured dish plausibly
 *     contains, and nothing that is a picture of the dish itself.
 *  4. `NEVER_DECOY` — garlic, which belongs in too much of the book to be a fair wrong answer
 *     anywhere.
 */
export function decoyCandidates(recipe: Recipe): string[] {
  const banned = new Set<string>([...recipe.ingredients, ...recipe.forbid]);
  return SHELF_INVENTORY.filter(
    (id) => !banned.has(id) && !NEVER_DEAL.has(id) && !NEVER_DECOY.has(id),
  );
}

/** The topic half of a pool id (`fruit-apple` → `fruit`); this mode's own ids share one topic. */
function topicOf(id: string): string {
  const dash = id.indexOf('-');
  return dash > 0 ? id.slice(0, dash) : id;
}

/**
 * `count` decoys for `recipe`, preferring ones from a topic the answers do not use.
 *
 * A shelf whose wrong tiles all come from the same basket as the right ones is a squinting
 * exercise; fruit against a soup and vegetables against a cake keeps the *reading* the hard part.
 * The preference is a sort, not a filter — a recipe that spans four topics still fills its shelf.
 */
export function pickDecoys(recipe: Recipe, count: number, rng: Rng = Math.random): string[] {
  if (count <= 0) return [];
  const answerTopics = new Set(recipe.ingredients.map(topicOf));
  const pool = shuffle(decoyCandidates(recipe), rng);
  const far = pool.filter((id) => !answerTopics.has(topicOf(id)));
  const near = pool.filter((id) => answerTopics.has(topicOf(id)));
  return [...far, ...near].slice(0, count);
}

/**
 * One round, ready to draw.
 *
 * The shelf is at least the recipe's own ingredients plus `RECIPE.minDecoys`: a rung whose tile
 * count is smaller than that (only reachable by a bad edit to the ladder) grows rather than
 * dealing a shelf on which everything is correct.
 */
export function dealRound(
  round: number, used: Set<string>, rng: Rng = Math.random, last?: string, lang?: string,
): DealtRound {
  const recipe = pickRecipe(round, used, rng, last, lang);
  used.add(recipe.id);

  const band = bandForRound(round);
  const wanted = Math.max(band.tiles, recipe.ingredients.length + RECIPE.minDecoys);
  const decoys = pickDecoys(recipe, wanted - recipe.ingredients.length, rng);

  return { recipe, shelf: shuffle([...recipe.ingredients, ...decoys], rng) };
}
